/**
 * Last line of defence against customer PII reaching the analytics pipeline.
 *
 * BCC order screens render end-consumer names and full shipping addresses
 * (`order-detail-dialog.tsx` shows name, address1/2, city, province, zip,
 * country), so the realistic failure is a well-meaning `...order` spread into
 * an event payload rather than anyone deliberately sending an address.
 *
 * The catalog already makes that a type error; this catches the runtime cases
 * types cannot see — `as` casts, API responses widened to `Record`, and
 * payloads built by string key.
 *
 * This is the SOLE control, not defence in depth. Breville moved to an
 * approval model for Freshpaint properties because PII was reaching Mixpanel,
 * but whitelisting is recursive: one approval on `precisionEventData.bcc`
 * means everything beneath it flows through without further review. BCC has
 * opted out of that check, so nothing downstream will catch what this misses.
 *
 * Behaviour is deliberately asymmetric:
 *
 *   - **dev and test — throw.** A denied property is a defect, and the person
 *     who can fix it is looking at the screen right now.
 *   - **production — drop the property, keep the event, log once.** A roaster
 *     mid-fulfilment must never see a crash because a label had a typo in its
 *     analytics. Losing one property beats losing the shipment.
 *
 * Generic names (`name`, `state`, `company`) are denied at every depth, which
 * does mean `{ order: { state: "ACTIVE" } }` reads as a violation even though
 * that `state` is a `PurchaseOrderState`. That is the right trade while this
 * is the only control: no catalog event declares a nested object (asserted in
 * `events.test.ts`), so a payload shaped like that is one the schema would
 * drop anyway — the throw names a payload that was already wrong. The cost
 * lands if an error-capture event is ever added, where `error.name` is
 * legitimate; tier the list then, with the real case in hand.
 */
const DENIED_PROPERTY_NAMES = [
  // Named explicitly in the agreed catalog's never-send list
  "useremail",
  "userfirstname",
  "userlastname",
  // The OIDC claims on the Salesforce user-info response
  // (`shared/src/services/salesforce/types.ts`). `given_name` and
  // `family_name` tokenise to a bare generic `name` with no customer qualifier,
  // so they have to be listed; `preferred_username` is caught by `username`.
  "givenname",
  "familyname",
  "nickname",
  "shippingdetails",
  "customeraccountname",
  "giftmessage",
  // Free text the end consumer typed at checkout: WooCommerce's
  // `customer_note`, Shopify's order `note`. Generic, so whole-key or
  // qualified only — `tastingNotes` and `roasterNotes` are roaster copy.
  "note",
  "notes",
  "externalordernumber",
  // People
  "name",
  "firstname",
  "lastname",
  "fullname",
  "customername",
  "recipient",
  "recipientname",
  // Who signed for the parcel — `signer` on a ShipEngine tracking event.
  "signer",
  "contactname",
  "company",
  // Contact
  "email",
  "emailaddress",
  "phone",
  "phonenumber",
  // Network identity. `browser_ip` on a Shopify order and
  // `customer_ip_address` on a WooCommerce one; `ip` as a token catches both
  // spellings that keep a boundary, the rest are the concatenated forms.
  "ip",
  "ipaddress",
  "browserip",
  // Address containers. Denying the container as well as its leaves drops the
  // whole subtree in one move, so an address shape whose field names we did
  // not anticipate never gets the chance to leak through one of these.
  // `ShippingDetails` (`shared/src/types/connection.ts`) nests `shippingAddress`
  // and keeps a `lastShippingAddress` audit copy, so both names are real.
  "shippingaddress",
  "lastshippingaddress",
  "billingaddress",
  // Address leaves — the `Address` shape, the carrier `AddressFields` one, and
  // the `line1`/`suburb` spellings a vendor or carrier payload may use. A
  // container name we never thought of (`shipTo`, `consignee`) is only caught
  // by these, so they carry the weight when the container misses.
  "address",
  "addressline1",
  "addressline2",
  "addressline3",
  "address1",
  "address2",
  "line1",
  "line2",
  "line3",
  "street",
  "street1",
  "street2",
  "street3",
  "city",
  "suburb",
  "locality",
  "province",
  "state",
  "zip",
  "postalcode",
  "postcode",
  // Coordinates pin an address to a doorstep as surely as its lines do.
  // `ShopifyAddress`, the ShipEngine address and the user-profile address
  // schema all carry these. The `lat`/`lng` abbreviations are left out: the
  // qualified-edge pass below would read `deliverylatency` as a `lat`, and no
  // address shape a BCC screen renders uses them.
  "latitude",
  "longitude",
] as const;

const DENIED = new Set<string>(DENIED_PROPERTY_NAMES);

/**
 * Generic words that compound innocently, so they are denied only as a whole
 * key and never as the tail of a longer one. `stepName` is a real catalog
 * property and `orderState` a real field on `PurchaseOrderConnection`; a bare
 * `name` or `state` is still denied.
 */
const COMPOUND_SAFE_NAMES = new Set(["name", "state", "company", "note", "notes"]);

/**
 * Qualifiers that make an otherwise generic name customer data. A qualifier in
 * front removes the ambiguity that keeps `name` and `state` whole-key-only, so
 * `shippingName` and `customerState` are denied where `stepName` and
 * `orderState` are not.
 */
const CUSTOMER_QUALIFIERS = [
  "shipping",
  "billing",
  "delivery",
  "customer",
  "recipient",
  "consignee",
  "buyer",
  "payer",
  "contact",
  "enduser",
  "user",
  "end",
] as const;

const QUALIFIERS = new Set<string>(CUSTOMER_QUALIFIERS);

/** `shipping_address_1`, `shippingAddress1` and `SHIPPING-ADDRESS-1` tokenise alike. */
const tokenize = (propertyName: string): string[] =>
  propertyName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((token) => token.toLowerCase());

/**
 * Which denied name a property name matches, if any — the name, not the key,
 * so a diagnostic can say what fired without echoing an arbitrary key.
 *
 * Three passes, because a flattened vendor payload is the common shape and a
 * Shopify order export carries no nested address at all:
 *
 *  1. the whole key, in any casing or separator style;
 *  2. a trailing run of tokens, since the qualifier always comes first
 *     (`shipping_address1`, `customer_email`). Tail-only is what keeps
 *     `emailPreferenceCount` allowed, and matching tokens rather than
 *     substrings is why `velocity` is not read as a `city`;
 *  3. a known customer qualifier against the front of the normalised name,
 *     for the spellings that have neither a case boundary nor a separator —
 *     `customeremail`, `SHIPPINGADDRESS1` — which tokenise to one token and so
 *     have no tail to test. Requiring a known qualifier is what keeps
 *     `velocity` out: `velo` is not one. A qualified token whose remainder is
 *     an open-ended compound (`customercompanyname`) is matched by a denied
 *     name at either edge of that remainder.
 */
const MAX_DENIED_NAME_LENGTH = Math.max(...DENIED_PROPERTY_NAMES.map((name) => name.length));

/** Longer than any property name a payload has business carrying. */
const MAX_KEY_LENGTH = 256;

/** Reported in place of a name for a key too long to be examined. */
const OVERSIZED_KEY_NAME = "oversized";

/**
 * Reported in place of a name for a key that is itself customer data.
 *
 * The vocabulary match only reads keys as property names, so a dictionary keyed
 * by the PII — `{ byRecipient: { "alice@example.com": true } }` — passed with
 * the key intact: nothing in it is a denied name, and a primitive value has
 * nothing further to screen. Two shapes are unambiguous enough to deny on
 * sight. No property name contains an `@`. And a key made only of phone
 * punctuation with nine or more digits is a phone number or a numeric ID —
 * nine, so an ISO date key (eight digits) stays allowed. A numeric-ID or
 * timestamp key is over-denied, which costs nothing while no catalog event
 * declares a nested object.
 *
 * A bare first name as a key is not detectable this way, and stays the known
 * limit the README names.
 */
const EMAIL_SHAPED_KEY_NAME = "email-shaped";
const PHONE_SHAPED_KEY_NAME = "phone-shaped";
const PHONE_CHARACTERS = /^[\d\s()+.-]+$/;
const MIN_PHONE_DIGITS = 9;

const matchPiiShapedKey = (propertyName: string): string | undefined => {
  if (propertyName.includes("@")) {
    return EMAIL_SHAPED_KEY_NAME;
  }

  const digits = propertyName.replace(/\D/g, "").length;
  if (digits >= MIN_PHONE_DIGITS && PHONE_CHARACTERS.test(propertyName)) {
    return PHONE_SHAPED_KEY_NAME;
  }

  return undefined;
};
const MAX_QUALIFIER_LENGTH = Math.max(...CUSTOMER_QUALIFIERS.map((qualifier) => qualifier.length));

/**
 * The longest denied token run anywhere in a key, or nothing.
 *
 * Any position, not just the tail. A trailing-only rule read
 * `shipping_address1` correctly but let `zip_code`, `city_name` and
 * `street_name` through — a denied name in the leading position followed by a
 * generic word, which is how plenty of flattened exports spell an address.
 *
 * Runs are grown from each start and abandoned once they outgrow the longest
 * name in the list, so the scan is linear in the token count rather than
 * quadratic, and bounded regardless.
 */
const findDeniedTokenRun = (tokens: readonly string[]): string | undefined => {
  let longestMatch: string | undefined;

  for (let start = 0; start < tokens.length; start += 1) {
    // A customer qualifier anywhere ahead of the run settles what the generic
    // words mean: `customerCompanyName` and `shippingStateCode` are customer
    // data, while `stepName` and `orderState` are not. Without this the
    // qualifier only counted in the concatenated pass, which needs the
    // remainder to be an exact entry — and `companyname` is not one.
    const qualified = tokens.slice(0, start).some((token) => QUALIFIERS.has(token));
    let run = "";
    let spanned = 0;

    for (const token of tokens.slice(start, start + MAX_DENIED_NAME_LENGTH)) {
      run += token;
      spanned += 1;
      if (run.length > MAX_DENIED_NAME_LENGTH) {
        break;
      }

      // A run covering the whole key belongs to the whole-key pass, so an
      // unqualified compound-safe name only counts there: `stepName` keeps
      // working while a bare `name` is still denied.
      const isWholeKey = start === 0 && spanned === tokens.length;
      const counts = qualified || !COMPOUND_SAFE_NAMES.has(run);
      if (!isWholeKey && counts && DENIED.has(run) && run.length > (longestMatch?.length ?? 0)) {
        longestMatch = run;
      }
    }
  }

  return longestMatch;
};

/**
 * A denied name at either edge of what follows a qualifier, in a single token
 * that lost its case boundaries — `customercompanyname`, `SHIPPINGSTATECODE`.
 *
 * The exact-remainder check needs `companyname` to be a list entry, and the
 * compounds are open-ended, so the remainder is read for a denied name at its
 * start or its end instead. Anchored rather than anywhere: the qualifier and
 * the end of the token are the only boundaries a flattened key still has, so
 * `shippingmethodnamespace` is not read as a `name`. Anchoring narrows the
 * guess without removing it — `shippingstatement` still reads as a `state` —
 * which is the same over-deny trade as `emailPreferenceCount`, confined to
 * keys that lost every boundary after a customer qualifier. The qualifier
 * settles the generic words here exactly as it does in the token-run pass.
 *
 * Per token, not on the joined key: a key that kept its boundaries
 * (`shippingStatementId`) is read precisely by the token-run pass and must not
 * be re-read loosely here. Each check is a set lookup on a slice no longer
 * than the longest name, so the cost is bounded by the list, not the token.
 */
/**
 * The shortest name the edge scan will read. `ip` is the one name below it: as
 * an edge it would read `customership` and `userrelationship` as addresses, and
 * it has no concatenated spelling worth that — `browserip` is listed outright.
 */
const MIN_EDGE_NAME_LENGTH = 3;

const findQualifiedTokenMatch = (token: string): string | undefined => {
  for (const qualifier of CUSTOMER_QUALIFIERS) {
    if (!token.startsWith(qualifier)) {
      continue;
    }

    const remainder = token.slice(qualifier.length);
    for (let length = Math.min(remainder.length, MAX_DENIED_NAME_LENGTH); length >= MIN_EDGE_NAME_LENGTH; length -= 1) {
      const leading = remainder.slice(0, length);
      if (DENIED.has(leading)) {
        return leading;
      }
      const trailing = remainder.slice(remainder.length - length);
      if (DENIED.has(trailing)) {
        return trailing;
      }
    }
  }

  return undefined;
};

const matchDeniedName = (propertyName: string): string | undefined => {
  // Checked before tokenising, because tokenising is itself unbounded: the
  // replace and the split scan the whole string before any window is chosen,
  // so one key of a million separators cost a million characters of work while
  // the node budget charged it a single entry. A name this long is not a
  // property name, so it is denied rather than examined — the safe direction,
  // and the schema would drop it regardless.
  if (propertyName.length > MAX_KEY_LENGTH) {
    return OVERSIZED_KEY_NAME;
  }

  const shapedMatch = matchPiiShapedKey(propertyName);
  if (shapedMatch !== undefined) {
    return shapedMatch;
  }

  const tokens = tokenize(propertyName);
  const normalized = tokens.join("");
  if (normalized.length <= MAX_DENIED_NAME_LENGTH && DENIED.has(normalized)) {
    return normalized;
  }

  const runMatch = findDeniedTokenRun(tokens);
  if (runMatch !== undefined) {
    return runMatch;
  }

  // An exact qualifier match cannot be longer than the two parts it is made
  // of, so a key past that length skips straight to the per-token scan.
  if (normalized.length <= MAX_QUALIFIER_LENGTH + MAX_DENIED_NAME_LENGTH) {
    const qualifier = CUSTOMER_QUALIFIERS.find(
      (candidate) => normalized.startsWith(candidate) && DENIED.has(normalized.slice(candidate.length))
    );
    if (qualifier) {
      return normalized.slice(qualifier.length);
    }
  }

  for (const token of tokens) {
    const match = findQualifiedTokenMatch(token);
    if (match !== undefined) {
      return match;
    }
  }

  return undefined;
};

/** Whether a single property name is denied, in any casing or separator style. */
export const isDeniedPropertyName = (propertyName: string): boolean => matchDeniedName(propertyName) !== undefined;

/**
 * What the root properties are called in a violation message, since they are
 * screened as a payload in their own right rather than as one event's.
 */
export const DEFAULT_PROPERTIES_SUBJECT = "default properties";

/**
 * Stands in for a value that must not appear in the output at all. Private to
 * this module, so no caller-supplied value can ever collide with it.
 */
const OMIT = Symbol("omit");

interface Screened<TValue> {
  /** The input unchanged when nothing was stripped, so a clean payload is never copied. */
  readonly value: TValue;
  /** Dotted paths of every denied property found, e.g. `order.destination.city`. */
  readonly deniedPaths: string[];
}

/**
 * The walk is bounded, and the limits are load-bearing rather than defensive
 * decoration.
 *
 * `ancestors` detects a true cycle — a value that is its own descendant — but
 * it is a path set, not a visited set, so it deliberately does not suppress a
 * diamond: the same object reached twice by different routes is screened
 * twice. That is correct for reporting and quadratic-to-exponential for a
 * shared graph. A React element or synthetic event spread into a payload is
 * exactly that shape, and a depth-22 diamond measured 2.7 seconds of
 * synchronous work on the main thread, so `nodes` caps total work no matter
 * how the references fan out.
 *
 * Every property and array element visited is charged against `nodes`, not
 * just the containers: a flat object with hundreds of thousands of primitive
 * keys reaches the same main-thread stall by a different route, and the screen
 * runs before Zod can discard it. "Visited" includes an inherited key that is
 * then skipped — enumerating it is the cost.
 *
 * The walk also takes nothing from the payload that it can get from an
 * intrinsic. Array indices are generated locally rather than read from
 * `entries()`, because a method off the prototype is the payload's to
 * override: it can feed a path, and it can spend as long as it likes before
 * yielding.
 *
 * `MAX_DEPTH` then keeps a self-nesting object from overflowing the stack,
 * which recursion can do before the node budget is spent.
 *
 * Both are far beyond any legitimate payload: a catalog event is flat, and a
 * spread order is three or four levels deep.
 */
const MAX_DEPTH = 16;
const MAX_NODES = 10_000;

/** Private: a payload the walk declined to finish. Never a PII finding. */
class UnscreenablePayloadError extends Error {}

interface WalkBudget {
  /** Values on the current path, for cycle detection. */
  readonly ancestors: Set<object>;
  /** Properties and elements visited so far, against `MAX_NODES`. */
  nodes: number;
  /**
   * Circular edges dropped, and the first place one was found.
   *
   * Counted rather than warned about on the spot: an array of references back
   * to the payload root is thousands of edges, and a `console.warn` each would
   * freeze the tab and flood captured console telemetry — the budget permits
   * nearly ten thousand of them in one `track`. One warning carries the count
   * and the first path, both trusted vocabulary.
   */
  circular?: { count: number; firstPath: string };
  /**
   * Why the walk gave up, recorded here rather than read back off the thrown
   * error.
   *
   * The payload chooses what it throws, and *anything* about that value can
   * throw again — a `name` accessor installed with `defineProperty`, a Proxy
   * trapping `getPrototypeOf`. That second throw would happen inside the catch
   * block, so it would escape `enforcePiiDenyList` and break the caller in
   * every environment. Writing the reason on an object we own means the catch
   * path never touches the payload at all, not even to classify it.
   */
  failure?: string;
}

/** Charges one visited property or element, and stops the walk when spent. */
const chargeEntry = (budget: WalkBudget): void => {
  budget.nodes += 1;
  if (budget.nodes > MAX_NODES) {
    budget.failure = `more than ${MAX_NODES} properties visited`;
    throw new UnscreenablePayloadError(budget.failure);
  }
};

const isWalkable = (value: unknown): value is object => typeof value === "object" && value !== null;

const joinPath = (parentPath: string, segment: string): string => (parentPath ? `${parentPath}.${segment}` : segment);

/**
 * **No diagnostic this module emits contains payload text.** Not a key, not a
 * value, not a message from an error the payload threw. A reported path is
 * built from trusted vocabulary only: a name from the list above, `*` for a
 * container, `[n]` for an array index.
 *
 * This was learned the hard way, three times over. A payload key is not safe
 * to print, because a key can be the PII — a dictionary keyed by email turns
 * a denied path into `lookup.alice@example.com.phone`, and the log that strips
 * the phone prints the address it was keyed against. Console output is
 * capturable: `lib/sentry/instrument.tsx` initialises Sentry with
 * `replayIntegration()`. Each attempt to decide *which* keys were safe — only
 * punctuation-free ones, only identifier-shaped ones — left a hole, because
 * `Alice_Smith` is identifier-shaped and `Error.name` is writable.
 *
 * So the question is not which payload text is safe to print. It is that none
 * of it is. What a developer needs is which rule fired and where in the
 * shape it fired, and both are available without quoting the payload.
 *
 * The cost is real: `{ order: { destination: { city } } }` reports
 * `*.*.city` rather than naming `order` and `destination`. The event name,
 * the denied name and the depth are enough to find it, and in development the
 * throw carries a stack trace to the call site.
 */
const REDACTED_SEGMENT = "*";

/**
 * Screens one value, recursing through objects and arrays.
 *
 * Every object is walked, not just plain ones: a payload built by `as` cast
 * can hold a class instance, and `Object.entries` on a `Date`, `Map` or DOM
 * node yields no own enumerable keys, so those pass through untouched rather
 * than being flattened. The copy is only ever made when something was
 * stripped, so a legitimate nested value keeps its prototype.
 */
const screenValue = (value: unknown, path: string, budget: WalkBudget, depth: number): Screened<unknown> => {
  if (!isWalkable(value)) {
    return { value, deniedPaths: [] };
  }

  // A cyclic payload cannot be screened to the end, and it could not have been
  // sent anyway: the transport serialises to JSON. Dropping the edge keeps the
  // rest of the payload screenable.
  if (budget.ancestors.has(value)) {
    budget.circular = { count: (budget.circular?.count ?? 0) + 1, firstPath: budget.circular?.firstPath ?? path };
    return { value: OMIT, deniedPaths: [] };
  }

  if (depth >= MAX_DEPTH) {
    budget.failure = `nested deeper than ${MAX_DEPTH} levels`;
    throw new UnscreenablePayloadError(budget.failure);
  }

  budget.ancestors.add(value);
  // Asserted, not inferred: `Object.entries` on a bare `object` resolves to the
  // `{}` overload, whose values come back as `any`. Narrowing here keeps the
  // walk typed, and it is sound — every key read is treated as `unknown`.
  const screened = Array.isArray(value)
    ? screenArray(value, path, budget, depth)
    : screenObject(value as Record<string, unknown>, path, budget, depth);
  budget.ancestors.delete(value);

  return screened;
};

const screenArray = (source: readonly unknown[], path: string, budget: WalkBudget, depth: number): Screened<readonly unknown[]> => {
  const deniedPaths: string[] = [];
  const screenedElements: unknown[] = [];
  let changed = false;

  // Indices are generated here, not taken from `source.entries()`. `entries`
  // lives on the prototype, so a payload can override it — and whatever such
  // an iterator yielded as an index went straight into a reported path, which
  // is how `*[alice@example.com].phone` reached a log that is supposed to
  // carry no payload text. A custom iterator can also do unbounded work before
  // its first yield, where the node budget would never see it.
  //
  // `length` cannot be subverted the same way: on an array exotic object it is
  // an own data property, never an accessor. A payload that lies about it only
  // changes how many locally generated indices are visited.
  for (let index = 0; index < source.length; index += 1) {
    chargeEntry(budget);
    const element = source[index];
    const screened = screenValue(element, `${path}[${index}]`, budget, depth + 1);
    deniedPaths.push(...screened.deniedPaths);

    if (screened.value === OMIT) {
      changed = true;
      continue;
    }

    changed = changed || screened.value !== element;
    screenedElements.push(screened.value);
  }

  return { value: changed ? screenedElements : source, deniedPaths };
};

const screenObject = (
  source: Record<string, unknown>,
  path: string,
  budget: WalkBudget,
  depth: number
): Screened<Record<string, unknown>> => {
  const deniedPaths: string[] = [];
  const screenedEntries: [string, unknown][] = [];
  let changed = false;

  // `for...in` with an own-key guard rather than `Object.entries`: entries
  // materialises the whole key/value array and reads every property up front,
  // so on a payload with hundreds of thousands of keys the budget could not
  // stop the work — nor the getters — until it had all already run. Iterating
  // lazily means a key past the budget is never read at all.
  //
  // Reading a value does invoke its getter; one that throws is caught by
  // `enforcePiiDenyList`, which fails closed.
  for (const key in source) {
    // Charged before the own-key guard, not after: `for...in` walks the
    // prototype chain, so a payload whose prototype carries a hundred thousand
    // enumerable keys would otherwise be enumerated in full with the budget
    // still at zero. Skipping a key is not the same as not visiting it.
    chargeEntry(budget);
    if (!Object.hasOwn(source, key)) {
      continue;
    }

    // Matched before the value is read, which is not just an optimisation: a
    // denied key needs no value, and reading one invokes its getter. A getter
    // that throws fails the whole payload closed, so reading a property we had
    // already decided to drop would turn one stripped field into a dropped
    // event — and would run a side effect for nothing.
    //
    // A denied key takes its whole subtree with it: there is nothing inside a
    // `shippingDetails` worth keeping, and reporting its children as well
    // would bury the one name the developer has to remove.
    const matchedName = matchDeniedName(key);
    if (matchedName !== undefined) {
      deniedPaths.push(joinPath(path, matchedName));
      changed = true;
      continue;
    }

    const value = source[key];
    const screened = screenValue(value, joinPath(path, REDACTED_SEGMENT), budget, depth + 1);
    deniedPaths.push(...screened.deniedPaths);

    if (screened.value === OMIT) {
      changed = true;
      continue;
    }

    changed = changed || screened.value !== value;
    screenedEntries.push([key, screened.value]);
  }

  return { value: changed ? Object.fromEntries(screenedEntries) : source, deniedPaths };
};

const newBudget = (root: Record<string, unknown>): WalkBudget => ({ ancestors: new Set<object>([root]), nodes: 0 });

/**
 * Every denied property in a payload, at any depth, as a dotted path.
 *
 * Nested, because the shapes that carry PII arrive nested: `ShippingDetails`
 * holds an `Address`, and a spread order holds both. `country` alone is not
 * PII; it is only denied as part of a fuller address.
 *
 * This is the strict form, for diagnostics and for tests: it throws if the
 * payload cannot be screened within the walk limits. `enforcePiiDenyList` is
 * the guarded entry point the app goes through.
 */
export const findDeniedProperties = (properties: Record<string, unknown>): string[] =>
  screenObject(properties, "", newBudget(properties), 0).deniedPaths;

export class AnalyticsPiiError extends Error {
  constructor(
    /** The event name, or `DEFAULT_PROPERTIES_SUBJECT` for the root properties. */
    readonly subject: string,
    readonly deniedProperties: string[]
  ) {
    super(
      `[analytics] "${subject}" carries denied properties: ${deniedProperties.join(", ")}. ` +
        `Customer PII must never reach the analytics pipeline — send an id instead.`
    );
    this.name = "AnalyticsPiiError";
  }
}

/**
 * Strips denied properties at any depth, or throws outside production.
 *
 * `NODE_ENV` is the switch every workspace already sets: webpack defines it
 * per build and Jest forces it to `"test"`.
 *
 * A payload the walk cannot finish returns no properties at all, in every
 * environment. That is not the asymmetry above and not a PII finding: it means
 * the screen does not know what the payload contains, so the only safe answer
 * is to send none of it and let the schema reject what is left. The walk must
 * never be the thing that breaks the page it is protecting.
 */
export const enforcePiiDenyList = (subject: string, properties: Record<string, unknown>): Record<string, unknown> => {
  const budget = newBudget(properties);
  let screened: Screened<Record<string, unknown>>;
  try {
    // One walk does both jobs: the paths decide what happens, and the screened
    // copy is what production sends.
    screened = screenObject(properties, "", budget, 0);
  } catch {
    // Deliberately unbound: the thrown value is the payload's, and reading
    // anything off it here could throw a second time — out of this catch, past
    // this function, into the caller. The reason comes off our own budget.
    console.error(
      `[analytics] could not screen "${subject}", so no properties were kept: ${budget.failure ?? "a property threw while being read"}`
    );

    return {};
  }

  if (budget.circular !== undefined) {
    console.warn(
      `[analytics] dropped ${budget.circular.count} circular propert${budget.circular.count === 1 ? "y" : "ies"} from "${subject}" ` +
        `(first at "${budget.circular.firstPath}") — a cyclic payload cannot be screened or sent`
    );
  }

  if (screened.deniedPaths.length === 0) {
    return screened.value;
  }

  if (process.env.NODE_ENV !== "production") {
    throw new AnalyticsPiiError(subject, screened.deniedPaths);
  }

  console.error(`[analytics] stripped denied properties from "${subject}": ${screened.deniedPaths.join(", ")}`);

  return screened.value;
};
